import React, { useState, useEffect } from 'react'
import { 
  Trash2, 
  RotateCcw, 
  Search, 
  Filter, 
  Calendar,
  FileText,
  Image,
  Music,
  Video,
  X,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive
} from 'lucide-react'
import recycleBinService from '../services/recycleBinService'
import toast from 'react-hot-toast'

const RecycleBinModal = ({ isOpen, onClose, onRestore }) => {
  const [items, setItems] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: 'all'
  })
  const [stats, setStats] = useState(null)

  // 加载回收站数据
  const loadRecycleBin = () => {
    setLoading(true)
    try {
      const allItems = recycleBinService.searchItems(searchQuery, filters)
      setItems(allItems)
      setFilteredItems(allItems)
      
      const recycleStats = recycleBinService.getRecycleBinStats()
      setStats(recycleStats)
    } catch (error) {
      console.error('加载回收站失败:', error)
      toast.error('加载回收站失败')
    } finally {
      setLoading(false)
    }
  }

  // 初始化加载
  useEffect(() => {
    if (isOpen) {
      loadRecycleBin()
    }
  }, [isOpen])

  // 搜索和过滤
  useEffect(() => {
    const filtered = recycleBinService.searchItems(searchQuery, filters)
    setFilteredItems(filtered)
  }, [searchQuery, filters])

  // 恢复项目
  const handleRestore = async (itemId) => {
    try {
      const result = recycleBinService.restoreItem(itemId)
      if (result.success) {
        toast.success('项目已恢复')
        if (onRestore) {
          onRestore(result.item)
        }
        loadRecycleBin()
      } else {
        toast.error(`恢复失败: ${result.error}`)
      }
    } catch (error) {
      console.error('恢复项目失败:', error)
      toast.error('恢复项目失败')
    }
  }

  // 永久删除项目
  const handlePermanentlyDelete = async (itemId) => {
    if (!confirm('确定要永久删除这个项目吗？此操作不可恢复！')) return

    try {
      const result = recycleBinService.permanentlyDeleteItem(itemId)
      if (result.success) {
        toast.success('项目已永久删除')
        loadRecycleBin()
      } else {
        toast.error(`删除失败: ${result.error}`)
      }
    } catch (error) {
      console.error('永久删除失败:', error)
      toast.error('永久删除失败')
    }
  }

  // 批量恢复
  const handleBatchRestore = async () => {
    if (selectedItems.length === 0) return

    try {
      const results = recycleBinService.batchOperation('restore', selectedItems)
      const successCount = results.filter(r => r.success).length
      
      toast.success(`已恢复 ${successCount} 个项目`)
      setSelectedItems([])
      loadRecycleBin()
    } catch (error) {
      console.error('批量恢复失败:', error)
      toast.error('批量恢复失败')
    }
  }

  // 批量永久删除
  const handleBatchPermanentlyDelete = async () => {
    if (selectedItems.length === 0) return
    if (!confirm(`确定要永久删除选中的 ${selectedItems.length} 个项目吗？此操作不可恢复！`)) return

    try {
      const results = recycleBinService.batchOperation('permanentlyDelete', selectedItems)
      const successCount = results.filter(r => r.success).length
      
      toast.success(`已永久删除 ${successCount} 个项目`)
      setSelectedItems([])
      loadRecycleBin()
    } catch (error) {
      console.error('批量删除失败:', error)
      toast.error('批量删除失败')
    }
  }

  // 清空回收站
  const handleEmptyRecycleBin = async () => {
    if (!confirm('确定要清空回收站吗？此操作不可恢复！')) return

    try {
      const result = recycleBinService.emptyRecycleBin()
      if (result.success) {
        toast.success('回收站已清空')
        loadRecycleBin()
      } else {
        toast.error(`清空失败: ${result.error}`)
      }
    } catch (error) {
      console.error('清空回收站失败:', error)
      toast.error('清空回收站失败')
    }
  }

  // 获取项目图标
  const getItemIcon = (type) => {
    switch (type) {
      case 'story': return <FileText className="w-5 h-5 text-blue-500" />
      case 'media': return <Image className="w-5 h-5 text-green-500" />
      default: return <FileText className="w-5 h-5 text-gray-500" />
    }
  }

  // 格式化时间
  const formatTime = (timeString) => {
    try {
      return new Date(timeString).toLocaleString('zh-CN')
    } catch {
      return timeString
    }
  }

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 获取年龄标签
  const getAgeLabel = (deletedAt) => {
    const now = new Date()
    const deleted = new Date(deletedAt)
    const daysDiff = (now - deleted) / (1000 * 60 * 60 * 24)
    
    if (daysDiff < 1) return { label: '今天', color: 'text-green-400' }
    if (daysDiff < 7) return { label: '本周', color: 'text-blue-400' }
    if (daysDiff < 30) return { label: '本月', color: 'text-yellow-400' }
    return { label: '更早', color: 'text-red-400' }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark rounded-2xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Trash2 className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-white">回收站</h2>
            {stats && (
              <span className="text-sm text-gray-400">
                ({stats.totalItems} 个项目, {stats.totalSizeKB} KB)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadRecycleBin}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title="刷新"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 统计信息 */}
        {stats && (
          <div className="p-4 bg-blue-500/10 border-b border-blue-500/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-blue-400 font-semibold">{stats.totalItems}</div>
                <div className="text-gray-400">总项目</div>
              </div>
              <div className="text-center">
                <div className="text-green-400 font-semibold">{stats.byType.story || 0}</div>
                <div className="text-gray-400">故事</div>
              </div>
              <div className="text-center">
                <div className="text-purple-400 font-semibold">{stats.byType.media || 0}</div>
                <div className="text-gray-400">媒资</div>
              </div>
              <div className="text-center">
                <div className="text-yellow-400 font-semibold">{stats.totalSizeKB} KB</div>
                <div className="text-gray-400">总大小</div>
              </div>
            </div>
          </div>
        )}

        {/* 工具栏 */}
        <div className="p-6 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-4">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索回收站项目..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* 类型过滤 */}
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">所有类型</option>
              <option value="story">故事</option>
              <option value="media">媒资</option>
            </select>

            {/* 时间过滤 */}
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              className="px-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">所有时间</option>
              <option value="1">今天</option>
              <option value="7">本周</option>
              <option value="30">本月</option>
            </select>

            {/* 批量操作 */}
            {selectedItems.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBatchRestore}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  恢复选中 ({selectedItems.length})
                </button>
                <button
                  onClick={handleBatchPermanentlyDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  永久删除
                </button>
              </div>
            )}

            {/* 清空回收站 */}
            <button
              onClick={handleEmptyRecycleBin}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              清空回收站
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Trash2 className="w-16 h-16 mb-4" />
              <p className="text-lg">回收站为空</p>
              <p className="text-sm">删除的项目会出现在这里</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => {
                const ageInfo = getAgeLabel(item.deletedAt)
                return (
                  <div
                    key={item.id}
                    className="flex items-center p-4 bg-dark-light/50 backdrop-blur-md rounded-lg border border-white/10 hover:border-primary/50 transition-all duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems([...selectedItems, item.id])
                        } else {
                          setSelectedItems(selectedItems.filter(id => id !== item.id))
                        }
                      }}
                      className="w-4 h-4 text-primary bg-dark-700 border-gray-600 rounded focus:ring-primary mr-4"
                    />

                    <div className="flex items-center gap-3 mr-4">
                      {getItemIcon(item.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-white truncate">
                          {item.data.title || item.data.name || '未命名'}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded ${ageInfo.color} bg-gray-700`}>
                          {ageInfo.label}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-400 mb-2">
                        <span className="mr-4">类型: {item.type === 'story' ? '故事' : '媒资'}</span>
                        <span className="mr-4">大小: {formatFileSize(item.size)}</span>
                        <span className="mr-4">原因: {item.reason}</span>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        删除时间: {formatTime(item.deletedAt)}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRestore(item.id)}
                        className="p-2 hover:bg-green-500/20 rounded transition-colors"
                        title="恢复"
                      >
                        <RotateCcw className="w-4 h-4 text-green-400" />
                      </button>
                      <button
                        onClick={() => handlePermanentlyDelete(item.id)}
                        className="p-2 hover:bg-red-500/20 rounded transition-colors"
                        title="永久删除"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RecycleBinModal
